/**
 * Road display-name formatting — PURE functions, deterministic from a road
 * `ref` prefix. Mirrors the legacy logic in `hooks/useRoadDetection.ts`
 * (`allRoads` useMemo) so the server-backed autocomplete renders identical
 * localized labels.
 *
 * The `t` function is passed in as a PARAMETER — these helpers MUST NOT call
 * `useTranslation` so they stay pure and reusable outside React.
 *
 * Single-source taxonomy: prefix → road type and the localized translation keys
 * both come from the canonical `constants/roadConstants.ts` (`ROAD_PREFIXES`,
 * `ROAD_TRANSLATION_KEYS`) so labels cannot drift from `utils/roadUtils.ts`.
 *
 * @module utils/roads/formatRoadDisplayName
 */

import type { TFunction } from 'i18next';
import type { RoadType } from '@/types/api/responses';
import { getRoadTypeFromShortName } from '@/utils/roadUtils';
import {
  ROAD_PREFIXES,
  ROAD_TRANSLATION_KEYS,
  RoadType as RoadTypeEnum,
} from '@/constants/roadConstants';

/**
 * Bridge between the API response `RoadType` string-union and the canonical
 * {@link RoadTypeEnum} used to key the shared `roadConstants` lookups. The enum
 * values are the same strings, so this map is an identity mapping that only
 * satisfies the type system.
 */
const RESPONSE_TYPE_TO_ENUM = {
  motorway: RoadTypeEnum.MOTORWAY,
  expressway: RoadTypeEnum.EXPRESSWAY,
  nationalRoad: RoadTypeEnum.NATIONAL_ROAD,
  voivodeshipRoad: RoadTypeEnum.VOIVODESHIP_ROAD,
} as const satisfies Record<RoadType, RoadTypeEnum>;

/**
 * Derive the concrete road type from a raw `ref`.
 * - `A*` → `motorway`
 * - `S*` → `expressway`
 * - `DW*` → `voivodeshipRoad`
 * - everything else (incl. `DK*` and bare national numbers) → `nationalRoad`
 *
 * Delegates to the canonical {@link getRoadTypeFromShortName} (same
 * `ROAD_PREFIXES` ladder) so the prefix mapping lives in ONE place, coalescing
 * its `null` default (unprefixed / bare numbers) to `nationalRoad`.
 */
export function roadTypeFromRef(ref: string): RoadType {
  return getRoadTypeFromShortName(ref) ?? 'nationalRoad';
}

/**
 * Build the short name shown in compact UI (e.g. badges).
 * - Motorways / expressways keep their `ref` verbatim (`A1`, `S7`).
 * - National roads are `DK`-prefixed unless the `ref` already is (`DK1`).
 * - Voivodeship roads are `DW`-prefixed unless the `ref` already is (`DW305`).
 */
export function roadShortName(ref: string, roadType: RoadType): string {
  if (roadType === 'nationalRoad') {
    return ref.startsWith(ROAD_PREFIXES.NATIONAL)
      ? ref
      : `${ROAD_PREFIXES.NATIONAL}${ref}`;
  }
  if (roadType === 'voivodeshipRoad') {
    return ref.startsWith(ROAD_PREFIXES.VOIVODESHIP)
      ? ref
      : `${ROAD_PREFIXES.VOIVODESHIP}${ref}`;
  }
  // Motorways and expressways use the raw ref.
  return ref;
}

/**
 * Build the full localized display name, e.g. `Autostrada A1`,
 * `Droga ekspresowa S7`, `Droga krajowa DK1`, `Droga wojewódzka DW305`.
 *
 * Resolves the prefix label through the shared {@link ROAD_TRANSLATION_KEYS} so
 * it cannot diverge from the reverse-geocoding labels in `roadUtils.ts`.
 */
export function formatRoadDisplayName(
  ref: string,
  roadType: RoadType,
  t: TFunction
): string {
  const prefix = t(ROAD_TRANSLATION_KEYS[RESPONSE_TYPE_TO_ENUM[roadType]]);
  return `${prefix} ${roadShortName(ref, roadType)}`;
}
